import { useProjectLockStore } from "@/frontend/store/ProjectLockStore";
import { I18n } from "@/i18n/I18n";
import { TriangleAlert } from "lucide-react";
import { useShallow } from "zustand/react/shallow";
import { BreadcrumbItem } from "../Breadcrumbs";
import { ProjectNav } from "./ProjectNav";
import { ProjectBreadcrumb } from "./ProjectBreadcrumb";

/**
 * Project chrome (breadcrumb + left hand navigation) shared by every page that
 * lives inside a project. Pages reached from a project but routed outside of
 * `/projects/:projectId` (a requirement, for example) can render themselves in
 * here so the project navigation stays available.
 */
export function ProjectLayout(props: { projectId: string, trailingBreadcrumbs?: BreadcrumbItem[], children: React.ReactNode }) {
  const { projectId, trailingBreadcrumbs, children } = props;

  const projectLock = useProjectLockStore(useShallow(state => state.projectLocks.find(projectLock => projectLock.projectId === projectId)));

  return (
    <div className="d-flex flex-column" style={{ height: '100vh' }}>
      <div className="flex-shrink-0">
        <ProjectBreadcrumb projectId={projectId} showProjectLink={!!trailingBreadcrumbs?.length} trailingItems={trailingBreadcrumbs} />
      </div>
      <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
        <ProjectNav projectId={projectId} />
        <div className="flex-grow-1 overflow-auto">
          {projectLock && (
            <div
              className="d-flex align-items-center gap-2 mx-3 mt-2"
              style={{
                backgroundColor: 'var(--yellow-200)',
                border: '1px solid var(--yellow-400)',
                borderRadius: '4px',
                padding: '12px 16px',
                boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.08)',
              }}
            >
              <TriangleAlert size={20} color="var(--yellow-700)" />
              <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--yellow-900)' }}>
                {I18n.get('projectLockWarningBanner', {
                  date: new Date(projectLock.lockDate).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  }),
                })}
              </span>
            </div>
          )}
          {children}
        </div>
      </div>
    </div>
  );
}
